import React from 'react';
import { motion } from 'framer-motion';
import { Target, Rocket, Globe2, Lightbulb, TrendingUp, Users2, Check } from 'lucide-react';
import { VisionHero } from '../components/PageHeroes';
import { useTheme } from '../context/ThemeContext';

const Vision = () => {
  const { isDarkMode } = useTheme();

  const pillars = [
    {
      icon: Target,
      title: 'Our Goal',
      description: 'To become the platform every UK business turns to first — for payroll, finance, and HR alike — not just one more tool in a crowded stack.'
    },
    {
      icon: Rocket,
      title: "Where We're Headed",
      description: 'From payroll compliance to a complete operating system for running a business — invoicing, accounts, banking, and beyond — all under one roof.'
    },
    {
      icon: Globe2,
      title: 'Our Reach',
      description: 'Starting with UK businesses, built on deep expertise in HMRC and UK regulation, with the foundations to expand as our customers grow internationally.'
    }
  ];

  const drivers = [
    {
      icon: Lightbulb,
      title: 'Simplicity at Scale',
      description: 'Complex business admin — payroll, tax, accounts — made genuinely simple, no matter how large or fast-growing the business.'
    },
    {
      icon: TrendingUp,
      title: 'Compliance Without Compromise',
      description: 'As regulations evolve — HMRC, VAT, GDPR — we evolve with them automatically, so you never fall behind.'
    },
    {
      icon: Users2,
      title: 'Built With Our Customers',
      description: 'Every roadmap decision is shaped by real conversations with the businesses who use the platform every day.'
    }
  ];

  const outcomes = ['Simple', 'Secure', 'Effortless'];

  // Theme-aware palette, kept local to this component (not CSS classes) so it's
  // guaranteed correct regardless of any external stylesheet — same approach used
  // on Pricing. The Big Statement panel is deliberately excluded: it's already a
  // fixed dark navy gradient that reads correctly on both a light and dark page,
  // same reasoning as every other gradient CTA on the site.
  const p = {
    pillarsSectionBg: isDarkMode ? '#161b22' : '#f8fafc',
    cardBg: isDarkMode ? '#0d1117' : 'white',
    cardBorder: isDarkMode ? '#30363d' : '#e2e8f0',
    cardShadow: isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(15, 23, 42, 0.05)',
    heading: isDarkMode ? '#e6edf3' : '#0f1d3a',
    body: isDarkMode ? '#8b949e' : '#64748b',
    driversSectionBg: isDarkMode ? '#0d1117' : 'white',
    eyebrowBg: isDarkMode ? 'rgba(13, 162, 124, 0.15)' : '#e3f7f0',
    eyebrowText: isDarkMode ? '#2dd4bf' : '#0b7e62',
    rowBg: isDarkMode ? '#161b22' : '#f8fafc',
    rowBorder: isDarkMode ? '#30363d' : '#e2e8f0',
    driverIconBg: isDarkMode ? 'rgba(13, 162, 124, 0.15)' : 'rgba(13, 162, 124, 0.1)',
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <VisionHero />

      {/* ===== PILLARS ===== */}
      <section style={{ padding: '5rem 1.5rem', background: p.pillarsSectionBg }}>
        <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } } }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.75rem' }}
          >
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={idx}
                  variants={{ hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } }}
                  whileHover={{ y: -6 }}
                  style={{
                    background: p.cardBg,
                    border: `1px solid ${p.cardBorder}`,
                    borderRadius: '1rem',
                    padding: '2.25rem 2rem',
                    boxShadow: p.cardShadow,
                    transition: 'box-shadow 0.25s ease',
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '0.75rem',
                      background: 'linear-gradient(135deg, #0da27c 0%, #06b6d4 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '1.5rem',
                      boxShadow: '0 6px 16px rgba(13, 162, 124, 0.3)',
                    }}
                  >
                    <Icon size={24} />
                  </div>
                  <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: p.heading, margin: '0 0 0.75rem' }}>
                    {pillar.title}
                  </h3>
                  <p style={{ fontSize: '0.95rem', color: p.body, lineHeight: 1.7, margin: 0 }}>
                    {pillar.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== BIG STATEMENT — fixed dark panel, correct in both themes already ===== */}
      <section style={{ padding: '5.5rem 1.5rem', background: 'linear-gradient(135deg, #0f1d3a 0%, #16284f 100%)', position: 'relative', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            top: '-120px',
            left: '-100px',
            width: '340px',
            height: '340px',
            background: 'radial-gradient(circle, rgba(13, 162, 124, 0.25) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-140px',
            right: '-100px',
            width: '360px',
            height: '360px',
            background: 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{ maxWidth: '820px', margin: '0 auto', textAlign: 'center', position: 'relative' }}
        >
          <p style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7dd3fc', margin: '0 0 1.5rem' }}>
            The world we're building toward
          </p>
          <h2 style={{ fontSize: 'clamp(1.7rem, 3.5vw, 2.5rem)', fontWeight: 700, lineHeight: 1.4, color: '#f1f5f9', margin: '0 0 2.25rem' }}>
            A world where running a business — payroll, finance, and everything in between — is{' '}
            simple, secure, and effortless for every UK company.
          </h2>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {outcomes.map((word) => (
              <span
                key={word}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  color: '#f1f5f9',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  padding: '0.5rem 1.1rem',
                  borderRadius: '999px',
                }}
              >
                <Check size={14} color="#2dd4bf" />
                {word}
              </span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ===== WHAT DRIVES US ===== */}
      <section style={{ padding: '5.5rem 1.5rem', background: p.driversSectionBg }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center', marginBottom: '3.5rem' }}
          >
            <p
              style={{
                display: 'inline-block',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: p.eyebrowText,
                background: p.eyebrowBg,
                padding: '0.35rem 1rem',
                borderRadius: '999px',
                margin: '0 0 1rem',
              }}
            >
              What drives us
            </p>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.1rem)', fontWeight: 700, color: p.heading, margin: 0 }}>
              Why we do this
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } } }}
            style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}
          >
            {drivers.map((driver, idx) => {
              const Icon = driver.icon;
              return (
                <motion.div
                  key={idx}
                  variants={{ hidden: { opacity: 0, x: -16 }, visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeOut' } } }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1.25rem',
                    background: p.rowBg,
                    border: `1px solid ${p.rowBorder}`,
                    borderRadius: '0.875rem',
                    padding: '1.5rem 1.75rem',
                  }}
                >
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '0.65rem',
                      background: p.driverIconBg,
                      color: '#0da27c',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: p.heading, margin: '0 0 0.4rem' }}>
                      {driver.title}
                    </h3>
                    <p style={{ fontSize: '0.92rem', color: p.body, lineHeight: 1.65, margin: 0 }}>
                      {driver.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Vision;